import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ClinicalOnboardingService } from '../../../services/clinical-onboarding.service';
import { ToastService } from '../../../services/general/toast.service';
import { ClinicianDocumentTemplate, ClinicianDocumentTemplateScope } from '../../../models/clinical-onboarding.model';
import {
  ModalManageClinicalOnboardingDocumentTemplateComponent,
} from '../../../modals/modal-manage-clinical-onboarding-document-template/modal-manage-clinical-onboarding-document-template.component';

/** Filtri elenco (feedback utente: "aggiungere filtri uno per ambito e un altro per tipologia"). */
const FILTER_ALL = 'ALL';

/**
 * Ridisegno "documenti flessibili" (feedback utente: titolo libero, illimitati, "generici
 * oppure del solo clinico", "sola presa visione oppure restituzione file"): elenco dei
 * ClinicianDocumentTemplate della survey, in ordine di visualizzazione (sort_order).
 * Sostituisce il vecchio schema a 4 slot fissi + OTHER (ClinicianDocumentType) - qui non
 * c'e' piu' nessun tipo predefinito, ogni riga e' un documento definito dal gestore.
 *
 * Elenca solo i documenti di survey (owner_candidate_id nullo lato backend, vedi
 * listDocumentTemplates) - i documenti "privati" di un singolo clinico (DBEK-412) si
 * gestiscono dalla scheda di quel clinico, mai da qui.
 */
@Component({
  selector: 'app-clinical-onboarding-document-templates',
  templateUrl: './clinical-onboarding-document-templates.component.html',
  styleUrls: ['./clinical-onboarding-document-templates.component.scss'],
  standalone: false,
})
export class ClinicalOnboardingDocumentTemplatesComponent implements OnChanges {
  @Input() surveyId: string | number;

  readonly ClinicianDocumentTemplateScope = ClinicianDocumentTemplateScope;
  readonly FILTER_ALL = FILTER_ALL;
  templates: ClinicianDocumentTemplate[] = [];
  loading = false;
  removingId: number = null;

  scopeFilter: string = FILTER_ALL;
  requiresUploadFilter: string = FILTER_ALL;

  get filteredTemplates(): ClinicianDocumentTemplate[] {
    return this.templates.filter((template) => {
      if (this.scopeFilter !== FILTER_ALL && template.scope !== this.scopeFilter) {
        return false;
      }
      if (this.requiresUploadFilter !== FILTER_ALL && String(template.requires_upload) !== this.requiresUploadFilter) {
        return false;
      }
      return true;
    });
  }

  constructor(
    private clinicalOnboardingService: ClinicalOnboardingService,
    private dialog: MatDialog,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.surveyId && this.surveyId) {
      this.load();
    }
  }

  load(): void {
    this.loading = true;
    this.clinicalOnboardingService.getDocumentTemplates(
      this.surveyId,
      (data) => {
        this.loading = false;
        this.templates = data || [];
      },
      () => {
        this.loading = false;
      },
    );
  }

  openCreate(): void {
    const dialogRef = this.dialog.open(ModalManageClinicalOnboardingDocumentTemplateComponent, {
      data: { surveyId: this.surveyId },
      panelClass: 'document-template-dialog',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.load();
      }
    });
  }

  /** Feedback utente: "non e' possibile visualizzare il file caricato" - solo per GENERIC (l'unico con un file proprio). */
  viewFile(template: ClinicianDocumentTemplate): void {
    this.clinicalOnboardingService.openDocumentTemplateFile(this.surveyId, template.id);
  }

  openEdit(template: ClinicianDocumentTemplate): void {
    const dialogRef = this.dialog.open(ModalManageClinicalOnboardingDocumentTemplateComponent, {
      data: { surveyId: this.surveyId, template },
      panelClass: 'document-template-dialog',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.load();
      }
    });
  }

  /** Stesso pattern gia' in uso (nessun dialog di conferma qui, coerente col vecchio removeOtherTemplate). */
  remove(template: ClinicianDocumentTemplate): void {
    this.removingId = template.id;
    this.clinicalOnboardingService.removeDocumentTemplate(
      this.surveyId,
      template.id,
      () => {
        this.removingId = null;
        this.load();
      },
      () => {
        this.removingId = null;
      },
    );
  }
}
